import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { capitalizeFirstLetter, formatToPKR } from "@/lib/helper";
import { toast } from "@/components/ui/use-toast";
import { ErrorType } from "@/types";
import { Transaction, SellableItem } from "@/types/pos/transaction";
import {
  useCreateTransactionMutation,
  useGetTransactionByIdQuery,
  useUpdateTransactionMutation,
} from "@/services/transactionApi";
import { ReceiptExport } from "../sales_history/table/receipt-component";
import { useGetAllExtensionsQuery } from "@/services/extensionApi";
import { useNavigate } from "react-router-dom";
import { ProductStatus, POSProductType, SaleStatus } from "@/types/enums";
import { InvoiceDetails } from "@/types/invoice";
import LinkedMembersPage from "@/components/admin/members/details/invoice_and_linked_members/linked-members";
import { useGetInvoiceByIdQuery } from "@/services/invoiceApi";
import { resetInvoice } from "@/redux/invoiceSlice";
import { useDispatch } from "react-redux";
import CashSuggestion from "./cash-suggestion";
import { burnToken, mintToken } from ".";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useGetAllProductsQuery } from "@/services/productApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { ProductType } from "@/types/system-setting/product";
import { resetWalletInvoice } from "@/redux/walletRefundSlice";
import { useApprovedRefundRequestMutation } from "@/services/walletApi";
import { WalletRequestStatus } from "@/types/wallet";
import { ConfirmationDialog } from "@/components/ui/common/confirmation-dailog";
import { useUpdateBookingStatusMutation } from "@/services/facilityApis";

interface PaymentItem {
  payment_method_id: number;
  payment_method: string;
  cheque_number?: string;
  amount: number;
}

// Extended SellableItem for internal calculations (includes tax info)
type ExtendedSellableItem = SellableItem & {
  tax_rate?: number;
  tax_type?: string;
  discount_percentage?: number;
  base_price?: number;
  description?: string;
  total?: number;
  facility_slot_data?: any;
};

type CheckoutProps = {
  setShowCheckout: React.Dispatch<React.SetStateAction<boolean>>;
  watcher: Transaction;
  showCheckout: boolean;
  productPayload: ExtendedSellableItem[];
  customer: any;
  initialValues: Transaction;
  setProductPayload: React.Dispatch<
    React.SetStateAction<ExtendedSellableItem[]>
  >;
  setCustomer: React.Dispatch<React.SetStateAction<any>>;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  refetchMemberships: () => void;
  refetchProducts: () => void;
  refetchEvents: () => void;
};

export default function Checkout({
  setShowCheckout,
  watcher,
  showCheckout,
  productPayload,
  customer,
  initialValues,
  setProductPayload,
  setCustomer,
  setInputValue,
  refetchMemberships,
  refetchProducts,
  refetchEvents,
}: CheckoutProps) {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const [query, setQuery] = useState("");
  const [srbMessage, setSRBMessage] = useState<string>("");
  const walletTransaction = useSelector(
    (state: RootState) => state.wallet.invoice,
  );
  const [isSuggestionOpen, setSuggestionOpen] = useState<boolean>(false);
  const [isCheckMethodSelected, setIsCheckMethodSelected] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const [change, setChangeAmount] = useState<number>(watcher.change_amount);
  const [amountReceived, setAmountReceived] = useState<number>(0);
  const { id } = useParams();
  const dispatch = useDispatch();
  const [transactionId, setTransactionId] = useState<number | null>(null);
  const [invoiceId, setInvoiceId] = useState<number | null>(null);
  const { data: enabledPayments } = useGetAllExtensionsQuery(
    { org_id: orgId, query: `type=payment&for_admin=true&is_active=true` },
    {
      skip: !showCheckout,
    },
  );
  const [payments, setPayments] = useState<PaymentItem[]>([]);
  const [totalReached, setTotalReached] = useState<boolean>(false);
  const [preSelect, setPreSelect] = useState<string>("");
  const [selectedInvoiceData, setSelectedInvoiceData] = useState<
    InvoiceDetails | undefined
  >(undefined);
  const [openLinkedMembers, setOpenLinkedMembers] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState<string>(
    watcher.total.toFixed(2) + "",
  );
  const [approveRequest, { isLoading: isApproving }] =
    useApprovedRefundRequestMutation();
  const [createTransaction, { isSuccess, isUninitialized }] =
    useCreateTransactionMutation();
  const [updateTransaction] = useUpdateTransactionMutation();
  const [updateBookingStatus] = useUpdateBookingStatusMutation();
  const [remainingAmount, setRemainingAmount] = useState<number>(watcher.total);
  const [currentInputAmount, setCurrentInputAmount] = useState<string>(
    String(watcher.total),
  );
  const [selectedMethod, setSelectedMethod] = useState<number | null>(null);
  const [isOpenConfirmation, setIsOpenConfirmation] = useState<boolean>(false);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);

  const { data: productList } = useGetAllProductsQuery({
    org_id: orgId,
    query: query,
  });
  const productListData = useMemo(() => {
    return Array.isArray(productList?.data) ? productList?.data : [];
  }, [productList]);

  const hasAssignableItems = useMemo(() => {
    return productPayload.some(
      (product) =>
        product.item_type === POSProductType.Membership ||
        (product.item_type === POSProductType.Product &&
          productListData?.find((p) => p.id === product.item_id)?.type !==
            ProductType.Wallet),
    );
  }, [productPayload, productListData]);

  useEffect(() => {
    if (watcher.total > 0) {
      const taxAmountToRemove =
        watcher.transaction_type == SaleStatus.Refund ? watcher.tax_amount : 0;
      setCurrentInputAmount(
        (watcher.total - Number(taxAmountToRemove)).toString(),
      );
      setRemainingAmount(watcher.total - Number(taxAmountToRemove));
    }
  }, [watcher.total]);

  const {
    formState: { errors, isSubmitting, isSubmitted },
    register,
    handleSubmit,
    reset,
    setValue,
  } = useFormContext<Transaction>();

  const { data: transactionData } = useGetTransactionByIdQuery(
    { transaction_id: transactionId as number, org_id: orgId },
    { skip: !transactionId },
  );

  const { data: invoicedata, refetch } = useGetInvoiceByIdQuery(
    {
      id: invoiceId as number,
      org_id: orgId,
    },
    {
      skip: !invoiceId || !orgId,
    },
  );

  const enabledPaymentsData = useMemo(() => {
    return Array.isArray(enabledPayments?.data) ? enabledPayments?.data : [];
  }, [enabledPayments]);

  const handleAmountChange = (value: string) => {
    const numValue = parseFloat(value) || 0;

    // Don't allow negative values
    if (numValue < 0) {
      return;
    }

    // Validate against remaining amount
    if (numValue > remainingAmount) {
      toast({
        variant: "destructive",
        title: "Amount exceeds remaining balance",
        description: `Maximum amount allowed: ${formatToPKR(remainingAmount)}`,
      });
      setCurrentInputAmount(remainingAmount.toString());
      return;
    }

    // Update current input
    setCurrentInputAmount(value);
  };

  const combinePayments = (
    existingPayments: PaymentItem[],
    methodId: number,
    methodName: string,
    newAmount: number,
  ): PaymentItem[] => {
    const existingPaymentIndex = existingPayments.findIndex(
      (p) => p.payment_method_id === methodId,
    );

    if (existingPaymentIndex > -1) {
      // Update existing payment
      const updatedPayments = [...existingPayments];
      updatedPayments[existingPaymentIndex] = {
        ...updatedPayments[existingPaymentIndex],
        amount: updatedPayments[existingPaymentIndex].amount + newAmount,
      };
      return updatedPayments;
    }

    // Add new payment
    return [
      ...existingPayments,
      {
        payment_method_id: methodId,
        payment_method: methodName.toLowerCase(),
        amount: newAmount,
      },
    ];
  };

  // Add this helper function to calculate total payments excluding cash
  const getTotalNonCashPayments = (payments: PaymentItem[]) => {
    return payments
      .filter((p) => p.payment_method.toLowerCase() !== "cash")
      .reduce((sum, p) => sum + p.amount, 0);
  };

  // Add this helper function to get total cash payments
  const getTotalCashPayments = (payments: PaymentItem[]) => {
    return payments
      .filter((p) => p.payment_method.toLowerCase() === "cash")
      .reduce((sum, p) => sum + p.amount, 0);
  };

  const handlePaymentMethodSelect = (methodId: number, methodName: string) => {
    setSelectedMethod(methodId);

    // For cash payments
    if (
      methodName.toLowerCase() === "cash" &&
      watcher.transaction_type !== SaleStatus.Refund
    ) {
      const amount = parseFloat(currentInputAmount);
      if (amount > 0) {
        setSuggestionOpen(true);
      }
      return;
    }

    // For check payments
    if (methodName.toLowerCase() === "cheque") {
      const amount = parseFloat(currentInputAmount);
      if (amount > 0) {
        setSelectedMethod(methodId); // Store the method ID for later use
        setIsCheckMethodSelected(true);
        return;
      }
    }

    // For other non-cash payments
    const paymentAmount = parseFloat(
      currentInputAmount || remainingAmount.toString(),
    );
    if (paymentAmount > 0) {
      const updatedPayments = combinePayments(
        payments,
        methodId,
        methodName.toLowerCase(),
        paymentAmount,
      );

      const totalPaid = updatedPayments.reduce((sum, p) => sum + p.amount, 0);
      const newRemainingAmount = Math.max(0, watcher.total - totalPaid);

      setPayments(updatedPayments);
      setRemainingAmount(newRemainingAmount);
      setCurrentInputAmount(
        newRemainingAmount > 0 ? newRemainingAmount.toString() : "0",
      );
      setSelectedMethod(null);
    }
  };

  const handleRemovePayment = (paymentMethodId: number) => {
    const paymentToRemove = payments.find(
      (p) => p.payment_method_id === paymentMethodId,
    );

    if (paymentToRemove) {
      const updatedPayments = payments.filter(
        (p) => p.payment_method_id !== paymentMethodId,
      );
      const totalPaid = updatedPayments.reduce((sum, p) => sum + p.amount, 0);

      // If removing cash payment, reset change and amount received
      if (paymentToRemove.payment_method.toLowerCase() === "cash") {
        setChangeAmount(0);
        setAmountReceived(totalPaid); // Reset to just the non-cash payments
      }

      setPayments(updatedPayments);
      setRemainingAmount(watcher.total - totalPaid);
      setCurrentInputAmount((watcher.total - totalPaid).toString());
    }
  };

  const paymentQuantity = watcher.items.reduce(
    (acc: number, payment: SellableItem) => acc + payment.quantity,
    0,
  );

  const handleCancel = () => {
    if (!isConfirming) {
      setIsOpenConfirmation(false);
      setSRBMessage("");
    }
  };

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await placeOrder(true);
      setIsOpenConfirmation(false);
      setIsConfirming(false);
      setSRBMessage("");
    } catch (error) {
      console.error("Error in confirm place order",error);
    } finally {
      setIsConfirming(false);
      setIsOpenConfirmation(false);
    }
  };

  const onlyProductItems =
    watcher.items.length > 0 &&
    watcher.items.every((item) => item.item_type === POSProductType.Product);

  const onlyEventsItems =
    watcher.items.length > 0 &&
    watcher.items.every((item) => item.item_type === POSProductType.Event);

  const onlyFacilitiesItems =
    watcher.items.length > 0 &&
    watcher.items.every((item) => item.item_type === POSProductType.Facilities);

  const placeOrder = async (confirm = false) => {
    const payload = {
      ...watcher,
      payments,
      total: Math.round(watcher.total), // Round the total
      status: ProductStatus.Paid,
      change_amount: change, // Add change amount to payload
      amount_received: amountReceived,
      submitted_to_authority: !confirm,
    };

    // Calculate total amount received from all payments
    const totalReceived = payments.reduce(
      (sum, payment) => sum + payment.amount,
      0,
    );
    const roundedTotal = Math.round(watcher.total);

    if (payload.payments.length === 0) {
      toast({
        variant: "destructive",
        title: "Please add at least one payment method and amount",
      });
      return;
    }

    // Check if total received is less than the rounded total
    if (totalReceived < roundedTotal) {
      toast({
        variant: "destructive",
        title: "Amount insufficient. Please enter the correct amount.",
      });
      return;
    }

    if (payload.transaction_type == SaleStatus.Refund) {
      // Get item descriptions from productPayload (ExtendedSellableItem) since items don't have description
      const formattedText = payload.items
        .map((item) => {
          const extendedItem = productPayload.find(
            (p) => p.item_id === item.item_id && p.item_type === item.item_type,
          );
          const itemDescription =
            extendedItem?.description || `Item ${item.item_id}`;
          return `• Quantity ${item.quantity} refunded for this membership plan: ${capitalizeFirstLetter(itemDescription)}`;
        })
        .join("<\br>");

      payload.refund_notes = formattedText;
    }

    try {
      if (!payload.id) {
        const resp = await createTransaction(payload).unwrap();
        if (resp) {
          if (
            (resp as any).srb_submission_required === true &&
            (resp as any).status_code == 409
          ) {
            setIsOpenConfirmation(true);
            setSRBMessage((resp as any).detail || "");
            return;
          }

          if (watcher.transaction_type != SaleStatus.Refund) {
            const toastTitle =
              onlyProductItems || onlyEventsItems
                ? "Transaction successful"
                : !payload.items.some(
                      (item) => item.item_type === POSProductType.Facilities,
                    )
                  ? paymentQuantity > 1 || payload.is_business
                    ? "Receipt created successfully, please assign the membership to relevant users through the assign membership button"
                    : "Receipt created successfully and membership assigned"
                  : "Receipt created successfully and Facility slot has been booked.";

            toast({
              variant: "success",
              title: toastTitle,
            });
          } else {
            // Handle refund case for wallet products
            const hasWalletProduct = productPayload.some(
              (product) =>
                product.item_type === POSProductType.Product &&
                productListData?.find((p) => p.id === product.item_id)?.type ===
                  ProductType.Wallet,
            );

            if (hasWalletProduct && customer?.wallet_address) {
              try {
                // Find all wallet products in the transaction
                const walletProducts = productPayload.filter(
                  (product) =>
                    product.item_type === POSProductType.Product &&
                    productListData?.find((p) => p.id === product.item_id)
                      ?.type === ProductType.Wallet,
                );

                if (walletProducts.length > 0) {
                  // Calculate the total amount from all wallet products
                  const totalWalletAmount = walletProducts.reduce(
                    (sum, product: ExtendedSellableItem) => {
                      const itemTotal =
                        (product as any).total ||
                        (product.price || 0) * product.quantity;
                      return sum + itemTotal;
                    },
                    0,
                  );

                  // Create burn data object
                  const burnData = {
                    org_id: orgId,
                    address: customer.wallet_address,
                    amount: totalWalletAmount,
                  };

                  const burnResponse = await burnToken(burnData);
                  if (burnResponse) {
                    approveRequest({
                      id: walletTransaction?.mainRefund_id as number,
                      status: WalletRequestStatus.APPROVED,
                      org_id: orgId,
                    }).unwrap();
                  }

                  toast({
                    variant: "success",
                    title:
                      "Refund processed and Wallet Amount Refunded successfully",
                  });
                }
              } catch (error) {
                console.error("Failed to burn token:", error);
                toast({
                  variant: "destructive",
                  title: "Error processing refund",
                  description: "Failed to burn tokens. Please contact support.",
                });
              }
            }
          }
          setPreSelect("");
          setTransactionId(resp.id);
          setInvoiceId(resp.invoice_id as number);

          const hasWalletProduct = productPayload.some(
            (product) =>
              product.item_type === POSProductType.Product &&
              productListData?.find((p) => p.id === product.item_id)?.type ===
                ProductType.Wallet,
          );

          // If this is a wallet transaction, mint the token
          if (hasWalletProduct && customer?.wallet_address) {
            try {
              // Find all wallet products in the transaction
              const walletProducts = productPayload.filter(
                (product) =>
                  product.item_type === POSProductType.Product &&
                  productListData?.find((p) => p.id === product.item_id)
                    ?.type === ProductType.Wallet,
              );

              if (
                walletProducts.length > 0 &&
                watcher.transaction_type != SaleStatus.Refund
              ) {
                // Calculate the total amount from all wallet products
                const totalWalletAmount = walletProducts.reduce(
                  (sum, product: ExtendedSellableItem) => {
                    const price = product.price || 0;
                    return sum + price * product.quantity;
                  },
                  0,
                );

                // Create a single mint data object with combined amount
                if (resp.invoice_id) {
                  const mintData = {
                    user_id: customer.id,
                    transaction_id: resp.id,
                    invoice_id: resp.invoice_id,
                    jwt_token: localStorage.getItem("userToken") || "",
                    org_id: import.meta.env.VITE_ORG_ID,
                    address: customer.wallet_address,
                    amount: totalWalletAmount, // Combined amount from all wallet products
                    payment_method: payments
                      .map((p) => p.payment_method)
                      .join(" / "),
                    product_name: "Wallet Top Up",
                    product_description: "",
                  };

                  const mintResponse = await mintToken(mintData);
                }
              }
            } catch (error) {
              console.error("Failed to mint token:", error);
            }
          }
        }
      } else if (payload.id) {
        const resp = await updateTransaction(payload).unwrap();
        if (resp) {
          toast({
            variant: "success",
            title: !payload.items.some(
              (item) => item.item_type === POSProductType.Facilities,
            )
              ? paymentQuantity > 1
                ? "Receipt updated successfully, please assign the membership if any to relevant users through the assign membership button"
                : "Receipt created successfully and membership assigned"
              : "Receipt updated successfully and Facility slot has been booked.",
          });
          setPreSelect("");
          setTransactionId(resp.id);
          setInvoiceId(resp.invoice_id as number);
        }
      }
    } catch (error: unknown) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail || (typedError.data as { message?: string }).message}`,
        });
      } else {
        console.log("Error", { error });
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    } finally {
      setPaymentAmount("");
      // Refetch all product lists after a successful transaction
      await Promise.all([
        refetchMemberships(),
        refetchProducts(),
        refetchEvents(),
      ]);
    }
  };

  const handlePrintReceipt = async () => {
    if (transactionData) {
      const invoiceData = transactionData;
      const htmlContent = await ReceiptExport(invoiceData);

      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.width = "0px";
      iframe.style.height = "0px";
      iframe.style.border = "none";

      document.body.appendChild(iframe);

      const iframeDoc =
        iframe.contentWindow?.document || iframe.contentDocument;
      if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();

        iframe.onload = () => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();

          setTimeout(() => {
            document.body.removeChild(iframe);
            handleReset();
          }, 1000);
        };
      }
    }
  };

  const handleReset = () => {
    dispatch(resetInvoice());
    dispatch(resetWalletInvoice());
    reset(initialValues as Transaction);
    setCustomer(null);
    setProductPayload([]);
    setShowCheckout(false);
    setPaymentAmount("");
    setInputValue("");
    setPreSelect(""); // Reset preSelect to empty string
    setSelectedInvoiceData(undefined);
    setPayments([]);
    setTotalReached(false);
    setTransactionId(null);
    navigate("/admin/pos/sell/");
  };

  const handleAssignMemberships = () => {
    // Clear any pending booking state since we're moving to member assignment

    // Reset or clear any previous state if necessary
    setSelectedInvoiceData(undefined); // Clear previous invoice data
    setPreSelect(""); // Clear previous pre-select value
    setOpenLinkedMembers(true);
    setSelectedInvoiceData(invoicedata);
    setPreSelect(
      invoicedata?.member_id !== undefined ? String(invoicedata.member_id) : "",
    );
  };

  console.log({ payments });

  const totalDiscount = Math.round(watcher.items.reduce(
    (sum, discount) => sum + discount.discount,
    0,
  ));
  console.log({ totalDiscount });
  return (
    watcher && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <div>
          <h1 className="text-3xl font-semibold mb-4">Checkout</h1>
          <div className="mb-4">
            <p className="text-xl font-semibold mb-2">Customer Information</p>
            <p>
              Name: <span className="capitalize">{watcher.member_name}</span>
            </p>
            <p className="text-sm text-gray-400">
              Email: {watcher.member_email}
            </p>
            <p className="text-sm text-gray-400">
              Address: {watcher.member_address}
            </p>
          </div>

          <div className="flex justify-between items-center mb-2">
            <h2 className="text-xl font-semibold">Order Summary</h2>
            {productPayload.length > 0 &&
              (productPayload[0] as ExtendedSellableItem).tax_type && (
                <span
                  className={`text-xs font-medium px-2 py-1 rounded ${
                    (productPayload[0] as ExtendedSellableItem).tax_type ===
                    "inclusive"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-green-100 text-green-700"
                  }`}
                >
                  Tax{" "}
                  {(productPayload[0] as ExtendedSellableItem).tax_type ===
                  "inclusive"
                    ? "Inclusive"
                    : "Exclusive"}
                </span>
              )}
          </div>
          <div className="space-y-4">
            <div className="flex flex-col justify-between items-start">
              {productPayload.map(
                (product: ExtendedSellableItem, index: number) => {
                  const unitPrice = product.base_price ?? product.price ?? 0;
                  const taxRate = product.tax_rate || 0;
                  const originalSubtotal = unitPrice * product.quantity;
                  // NOTE: `product.discount` is already the line discount amount (not per-unit).
                  // For inclusive tax: product.total is the tax-inclusive amount after discount (Gross in formula)
                  // For exclusive tax: product.total is base + tax after discount
                  // Display price after discount but before tax
                  const displayTotal = originalSubtotal - (product.discount || 0);
                  return (
                    <div
                      key={index}
                      className="flex justify-between items-start gap-2 w-full"
                    >
                      <div className="flex-1">
                        <p className="text-lg font-medium capitalize">
                          {product.description || `Item ${product.item_id}`}
                          {product.tax_rate === -1 && (
                            <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-primary text-black rounded uppercase">
                              Donation
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500">
                          Quantity: {product.quantity}{" "}
                          {taxRate > 0 ? `• Tax: ${taxRate}%` : taxRate === -1 ? "• Tax: 0%" : ""}
                        </p>
                        {product.discount > 0 && (
                          <p className="text-xs text-gray-400 line-through">
                            {formatToPKR(originalSubtotal)}
                          </p>
                        )}
                      </div>
                      <div className="text-lg font-semibold">
                        {formatToPKR(displayTotal)}
                      </div>
                    </div>
                  );
                },
              )}
            </div>
            <Separator className="bg-gray-300" />
            <div className="space-y-0">
              <div className="flex justify-between items-center">
                <p className="pl-28">Net price</p>
                <p>{formatToPKR(watcher.sub_total)}</p>
              </div>
              <div className="flex justify-between items-center">
                <p className="pl-28">Discount</p>
                <p>
                  {formatToPKR(
                    totalDiscount > 0 ? -Number(totalDiscount) : totalDiscount,
                  )}
                </p>
              </div>
              <div className="flex justify-between items-center">
                <p className="pl-28">Subtotal</p>
                <p>
                  {formatToPKR(
                    Number(watcher.sub_total) - Number(watcher.discount),
                  )}
                </p>
              </div>
              {watcher.transaction_type != SaleStatus.Refund && (
                <div className="flex justify-between items-center">
                  <p className="pl-28">
                    Tax{" "}
                    {productPayload.length > 0 &&
                      productPayload[0].tax_type === "inclusive" && (
                        <span className="text-xs text-gray-500">
                          (included)
                        </span>
                      )}
                  </p>
                  <p>{formatToPKR(watcher.tax_amount)}</p>
                </div>
              )}
            </div>
            <Separator className="bg-gray-300" />
            <div className="flex justify-between items-center">
              <div className="text-lg font-semibold space-x-3">
                <span>Sales Total</span>
                <span className="!text-sm font-normal">
                  {paymentQuantity} Items
                </span>
              </div>
              <div className="text-lg font-semibold">
                {/* {formatToPKR(
                  (id && watcher.transaction_type === SaleStatus.Refund
                    ? -Number(watcher.total)
                    : watcher.total) as number
                )} */}
                {formatToPKR(
                  (watcher.transaction_type === SaleStatus.Refund
                    ? -Number(watcher.total)
                    : watcher.total) as number,
                )}
              </div>
            </div>
            <Separator className="bg-gray-300" />
            <p className="text-xl font-semibold mb-4">Payment Method</p>
            {payments.map((payment: PaymentItem, index: number) => (
              <div key={index} className="flex justify-between items-center">
                <p className="capitalize">
                  {payment.payment_method.toLowerCase() == "cash" && (
                    <i className="fa-solid fa-money-bill mr-2"></i>
                  )}
                  {payment.payment_method.toLowerCase() == "credit card" && (
                    <i className="fa-regular fa-credit-card mr-2"></i>
                  )}
                  {payment.payment_method.toLowerCase() == "bank transfer" && (
                    <i className="fa fa-building-columns mr-2"></i>
                  )}
                  {payment.payment_method}
                </p>
                <div className="font-semibold text-right flex gap-2 items-center">
                  <span>{formatToPKR(payment.amount)}</span>
                  {transactionId == null && (
                    <button
                      title="Remove payment method"
                      className="p-0"
                      onClick={() =>
                        handleRemovePayment(payment.payment_method_id)
                      }
                    >
                      <i className="fa-regular fa-trash-can text-red-500"></i>
                    </button>
                  )}
                </div>
              </div>
            ))}
            {Number(change) > 0 && (
              <>
                <Separator className="bg-gray-300" />
                <div className="flex justify-between items-center">
                  <div className="space-y-2">
                    <p className="text-xl font-semibold">Change</p>
                    {amountReceived > 0 && (
                      <p className="text-sm text-gray-500">
                        Amount Received: {formatToPKR(amountReceived)}
                      </p>
                    )}
                  </div>
                  <p>{formatToPKR(change)}</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* amount to pay section */}
        <form
          noValidate
          onSubmit={handleSubmit(() => placeOrder(false))}
          className="flex flex-col justify-start gap-5"
        >
          {transactionId == null ? (
            <div className="bg-white p-6 rounded-lg flex flex-col justify-start gap-x-6 space-y-4">
              <div className="flex flex-col xl:flex-row items-center justify-between gap-x-10 gap-y-4">
                <p className="text-2xl font-semibold text-nowrap">
                  Amount to{" "}
                  {watcher.transaction_type == SaleStatus.Refund
                    ? "Refund"
                    : "Pay"}
                </p>
                <div className="flex justify items-end">
                  <FloatingLabelInput
                    id="amount"
                    type="number"
                    className="h-10 max-w-48 p-2 text-right text-xl placeholder:text-xl placeholder:font-semibold placeholder:text-gray-400 font-semibold"
                    value={currentInputAmount}
                    placeholder={remainingAmount > 0 ? `Enter Amount` : "0"}
                    disabled={
                      remainingAmount <= 0 ||
                      watcher.transaction_type == SaleStatus.Refund
                    }
                    onChange={(e) => handleAmountChange(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 justify-evenly gap-3">
                  {enabledPaymentsData &&
                    enabledPaymentsData.length > 0 &&
                    enabledPaymentsData
                      .filter((method) => method.name !== "Wallet")
                      .map((method) => (
                        <Button
                          key={method.id}
                          onClick={() =>
                            handlePaymentMethodSelect(method.id, method.name)
                          }
                          // ${selectedMethod === method.id ? 'bg-blue-200' : 'bg-gray-200'}
                          className={` text-black px-4 py-2 text-nowrap w-full bg-gray-200`}
                          disabled={
                            (isSuccess && !isUninitialized) ||
                            remainingAmount <= 0 ||
                            !currentInputAmount
                          }
                        >
                          <span className="capitalize">{method.name}</span>
                        </Button>
                      ))}
                </div>
              </div>

              <div>
                <FloatingLabelInput
                  id="description"
                  label="Notes"
                  type="textarea"
                  rows={7}
                  className="custom-scrollbar col-span-2 peer-placeholder-shown:top-[10%]"
                  {...register("notes", {
                    maxLength: {
                      value: 200,
                      message: "Notes should not exceed 200 characters",
                    },
                  })}
                  error={errors.notes?.message}
                />
              </div>
              <div className="flex justify-end">
                {transactionId == null ? (
                  <div className="flex flex-col xl:flex-row w-full justify-end gap-3">
                    {!watcher.invoice_id &&
                      watcher.transaction_type !== SaleStatus.Refund && (
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setShowCheckout(false);
                            setInputValue("");
                          }}
                        >
                          Back
                        </Button>
                      )}
                    <LoadingButton
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      type="submit"
                      className="disabled:cursor-not-allowed text-nowrap"
                    >
                      {!isSubmitting && (
                        <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                      )}
                      Complete{" "}
                      {id && watcher.transaction_type == SaleStatus.Refund
                        ? "Refund"
                        : "Checkout"}
                    </LoadingButton>
                  </div>
                ) : (
                  <div className="flex flex-col xl:flex-row w-full justify-end gap-3">
                    <Button variant="ghost" onClick={handleReset}>
                      Back
                    </Button>
                    <Button onClick={handlePrintReceipt}>Print Receipt</Button>
                    {(!onlyProductItems &&
                      !onlyFacilitiesItems &&
                      id &&
                      watcher.transaction_type !== SaleStatus.Refund) ||
                      (!id &&
                        invoiceId &&
                        hasAssignableItems &&
                        !onlyProductItems &&
                        !onlyFacilitiesItems && (
                          <Button onClick={handleAssignMemberships}>
                            Assign Memberships
                          </Button>
                        ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center min-h-screen bg-white p-4">
              <div className="w-full max-w-md flex flex-col items-center">
                <p className="text-3xl font-medium text-slate-700 mb-12 text-center">
                  {watcher.transaction_type == SaleStatus.Refund
                    ? `Refund #${transactionId} recorded`
                    : `Payment #${transactionId} recorded`}{" "}
                  successfully
                </p>
                {change > 0 && (
                  <p className="text-2xl font-medium text-slate-700 mb-12 text-center">
                    Give {formatToPKR(change)} Change
                  </p>
                )}
                <div className="flex flex-col xl:flex-row w-full justify-end gap-3">
                  <Button variant="ghost" onClick={handleReset}>
                    Back
                  </Button>
                  <Button onClick={() => handlePrintReceipt()}>
                    Print Receipt
                  </Button>

                  {((id && watcher.transaction_type !== SaleStatus.Refund) ||
                    !id) &&
                    invoiceId &&
                    hasAssignableItems &&
                    !onlyProductItems &&
                    !onlyFacilitiesItems && (
                      <Button onClick={handleAssignMemberships}>
                        Assign Memberships
                      </Button>
                    )}
                </div>
              </div>
            </div>
          )}
        </form>

        <CashSuggestion
          isOpen={isSuggestionOpen}
          setOpen={setSuggestionOpen}
          amountToPay={parseFloat(currentInputAmount)}
          onPaymentSelected={(selectedAmount) => {
            // Get totals of existing payments
            const nonCashTotal = getTotalNonCashPayments(payments);
            const existingCashTotal = getTotalCashPayments(payments);
            const totalCurrentPaid = nonCashTotal + existingCashTotal;
            const actualRemaining = watcher.total - totalCurrentPaid;

            // Calculate new total cash amount
            const totalCashAmount = existingCashTotal + selectedAmount;

            let actualCashPayment = selectedAmount;
            let newChange = 0;

            // Case 1: Adding this cash amount still leaves remaining balance
            if (nonCashTotal + totalCashAmount < watcher.total) {
              // Just add the full selected amount
              actualCashPayment = selectedAmount;
              newChange = 0;

              // Case 2: This cash amount will complete or exceed the payment
            } else {
              // Calculate how much cash is actually needed
              const cashNeeded = watcher.total - nonCashTotal;
              // If we already have some cash, adjust the new payment
              actualCashPayment = cashNeeded - existingCashTotal;
              // Calculate change based on total cash provided
              newChange = totalCashAmount - cashNeeded;
            }

            // Update payments array with the new cash amount
            const updatedPayments = combinePayments(
              payments,
              selectedMethod || 0,
              "Cash",
              actualCashPayment,
            );

            // Calculate final remaining amount
            const totalPaid = updatedPayments.reduce(
              (sum, p) => sum + p.amount,
              0,
            );
            const newRemainingAmount = Math.max(0, watcher.total - totalPaid);

            // Update all states
            setPayments(updatedPayments);
            setChangeAmount(newChange);
            setAmountReceived(totalCashAmount); // Total cash received including previous cash
            setRemainingAmount(newRemainingAmount);
            setCurrentInputAmount(
              newRemainingAmount > 0 ? newRemainingAmount.toString() : "0",
            );
            setTotalReached(newRemainingAmount === 0);
            setSelectedMethod(null);
            setSuggestionOpen(false);
          }}
        />
        <LinkedMembersPage
          isOpen={openLinkedMembers}
          setOpen={setOpenLinkedMembers}
          invoiceId={selectedInvoiceData?.id as number}
        />

        <PayemntCheckNumberInput
          isOpen={isCheckMethodSelected}
          setOpen={(isOpen) => {
            if (!isOpen) {
              setSelectedMethod(null); // Reset selected method if dialog is closed
            }
            setIsCheckMethodSelected(isOpen);
          }}
          amount={parseFloat(currentInputAmount)}
          onSubmit={(checkNumber) => {
            if (selectedMethod) {
              const paymentAmount = parseFloat(currentInputAmount);
              const newPayment: PaymentItem = {
                payment_method_id: selectedMethod,
                payment_method: "Cheque",
                amount: paymentAmount,
                cheque_number: checkNumber,
              };

              const updatedPayments = [...payments, newPayment];
              const totalPaid = updatedPayments.reduce(
                (sum, p) => sum + p.amount,
                0,
              );
              const newRemainingAmount = Math.max(0, watcher.total - totalPaid);

              setPayments(updatedPayments);
              setRemainingAmount(newRemainingAmount);
              setCurrentInputAmount(
                newRemainingAmount > 0 ? newRemainingAmount.toString() : "0",
              );
              setSelectedMethod(null);
            }
          }}
        />
        <ConfirmationDialog
          isOpen={isOpenConfirmation}
          onClose={handleCancel}
          onConfirm={handleConfirm}
          title="SRB ID not generated"
          message={
            srbMessage != ""
              ? srbMessage
              : `Do you want to proceed with the transaction without SRB?`
          }
          isLoading={isConfirming}
        />
      </div>
    )
  );
}

interface PaymentCheckNumberInputProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmit: (checkNumber: string) => void;
  amount: number;
}

const PayemntCheckNumberInput = ({
  isOpen,
  setOpen,
  onSubmit,
  amount,
}: PaymentCheckNumberInputProps) => {
  const [checkNumber, setCheckNumber] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (!checkNumber.trim()) {
      setError("Check number is required");
      return;
    }
    onSubmit(checkNumber);
    setCheckNumber("");
    setError("");
    setOpen(false);
  };

  const handleClose = () => {
    setCheckNumber("");
    setError("");
    setOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Enter Cheque Number</DialogTitle>
          <DialogDescription>
            Amount to be paid: {formatToPKR(amount)}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="checkNumber">Check Number</Label>
            <Input
              id="checkNumber"
              placeholder="Enter check number"
              value={checkNumber}
              onChange={(e) => {
                setCheckNumber(e.target.value);
                setError("");
              }}
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit}>
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
